import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { EvaluationIssue } from '@/types/api'

interface FeedbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  issue: EvaluationIssue | null
  onSubmit: (reason: string, comment: string) => void
  isLoading?: boolean
}

const FEEDBACK_REASONS = [
  { value: 'not_an_error', label: 'Not an error', description: 'Content is correct or acceptable' },
  { value: 'detection_error', label: 'Detection error', description: 'Wrong context or misinterpretation' },
  { value: 'other', label: 'Other reason', description: 'Explain in the details below' },
]

const severityBadges: Record<string, string> = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-blue-100 text-blue-800',
}

export function FeedbackDialog({
  open,
  onOpenChange,
  issue,
  onSubmit,
  isLoading,
}: FeedbackDialogProps) {
  const [reason, setReason] = useState('')
  const [comment, setComment] = useState('')

  const handleSubmit = () => {
    onSubmit(reason, comment)
    setReason('')
    setComment('')
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setReason('')
      setComment('')
    }
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Report Detection Error</DialogTitle>
          <DialogDescription>
            Help us improve by explaining why this detected issue is incorrect.
          </DialogDescription>
        </DialogHeader>

        {issue && (
          <div className="p-3 bg-gray-50 rounded-lg text-sm mb-4">
            <div className="font-medium text-gray-900">{issue.title}</div>
            <div className="flex items-center gap-2 mt-1">
              <span className={cn('px-2 py-0.5 rounded text-xs font-medium', severityBadges[issue.severity] || 'bg-gray-100 text-gray-800')}>
                {issue.severity.toUpperCase()}
              </span>
              <span className="text-gray-500 text-xs">{issue.category}</span>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-3 block">
              Select a reason
            </Label>
            <div className="space-y-2">
              {FEEDBACK_REASONS.map(r => (
                <label
                  key={r.value}
                  className={cn(
                    'flex items-start p-3 border rounded-lg cursor-pointer transition-colors',
                    reason === r.value
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={(e) => setReason(e.target.value)}
                    className="mr-3 mt-0.5"
                  />
                  <div>
                    <span className="text-sm font-medium">{r.label}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">{r.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="comment" className="text-sm font-medium mb-2 block">
              Details <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="comment"
              placeholder="Please explain why this issue is incorrect..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              required
            />
            {reason && !comment.trim() && (
              <p className="text-xs text-red-500 mt-1">Details are required to submit your report.</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!reason || !comment.trim() || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Report'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
